(() => {
  if (customElements.get('info-bar-slider')) return;

  class InfoBarSlider extends HTMLElement {
    constructor() {
      super();
      
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onSectionSelect = this.onSectionSelect.bind(this);
      this.onSectionDeselect = this.onSectionDeselect.bind(this);
      this.onResize = this.onResize.bind(this);
      
      this.currentSlide = 0;
      this.autoplayTimer = null;
      this.isPaused = false;
      this.isInitialized = false;
    }

    connectedCallback() {
      // Wait for layout to complete
      setTimeout(() => {
        if (this.isConnected) {
          this.init();
        }
      }, 100);
    }

    init() {
      if (this.isInitialized) return;
      
      this.slider = this.querySelector('[data-slider]');
      this.slides = Array.from(this.querySelectorAll('.header-info-bar__slide'));
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.interval = parseInt(this.dataset.interval, 10) || 4000;

      if (!this.slider || this.slides.length === 0) {
        return;
      }

      this.isInitialized = true;
      this.setEventListeners();
      
      // Start autoplay only on mobile
      this.handleAutoplay();
    }

    handleAutoplay() {
      this.stopAutoplay();
      
      if (this.autoplayEnabled && this.isMobile()) {
        this.startAutoplay();
      }
    }

    disconnectedCallback() {
      this.removeEventListeners();
      this.stopAutoplay();
    }

    setEventListeners() {
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      window.addEventListener('resize', this.onResize);
      
      // Theme Editor events
      if (window.Shopify?.designMode) {
        document.addEventListener('shopify:section:select', this.onSectionSelect);
        document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
      }

      // Pause on user interaction (only on mobile)
      if (this.isMobile()) {
        this.slider.addEventListener('touchstart', () => this.pauseAutoplay(), { passive: true });
        this.slider.addEventListener('scroll', () => this.onScroll(), { passive: true });
      }
    }

    removeEventListeners() {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      window.removeEventListener('resize', this.onResize);
      
      if (window.Shopify?.designMode) {
        document.removeEventListener('shopify:section:select', this.onSectionSelect);
        document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
      }
    }

    onResize() {
      // Re-evaluate autoplay when window is resized
      this.isPaused = false;
      this.handleAutoplay();
    }

    onVisibilityChange() {
      if (document.hidden) {
        this.pauseAutoplay();
      } else if (!this.isPaused) {
        this.handleAutoplay();
      }
    }

    onSectionSelect(event) {
      if (this.contains(event.target)) {
        this.pauseAutoplay();
      }
    }

    onSectionDeselect(event) {
      if (this.contains(event.target)) {
        this.isPaused = false;
        this.handleAutoplay();
      }
    }

    onScroll() {
      // Update current slide based on scroll position
      const scrollLeft = this.slider.scrollLeft;
      const slideWidth = this.slides[0]?.offsetWidth || 0;
      
      if (slideWidth > 0) {
        this.currentSlide = Math.round(scrollLeft / slideWidth);
      }

      // Restart autoplay after user scroll
      if (!this.isPaused) {
        this.stopAutoplay();
        setTimeout(() => {
          if (!this.isPaused) {
            this.handleAutoplay();
          }
        }, 2000);
      }
    }

    startAutoplay() {
      this.stopAutoplay();
      
      this.autoplayTimer = setInterval(() => {
        this.goToNextSlide();
      }, this.interval);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    pauseAutoplay() {
      this.isPaused = true;
      this.stopAutoplay();
    }

    goToNextSlide() {
      if (!this.slider || this.slides.length === 0) return;

      const previousSlide = this.currentSlide;
      this.currentSlide = (this.currentSlide + 1) % this.slides.length;
      
      const slideWidth = this.slides[0].offsetWidth;
      const scrollPosition = this.currentSlide * slideWidth;
      
      // Use instant transition when looping from last to first slide
      const isLooping = previousSlide === this.slides.length - 1 && this.currentSlide === 0;
      
      this.slider.scrollTo({
        left: scrollPosition,
        behavior: isLooping ? 'auto' : 'smooth'
      });
    }

    isMobile() {
      return window.innerWidth < 750;
    }
  }

  customElements.define('info-bar-slider', InfoBarSlider);
})();
